import { usePortfolioOverviewAssetAllocation, useWealthOverview, type InvestmentAccountDetail } from '@egw/api'
import {
  ActionTile,
  Alert,
  AlertType,
  Button,
  ButtonVariant,
  Collapse,
  ModalDrawer,
  PageHeaderRedesign,
  useModalState,
} from '@egw/components'
import { DEFAULT_CURRENCY_CODE, type FeatureFlags, WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { Currency, getCurrencySymbol, getLastRefreshedText } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { type FC, useMemo } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { configClient } from '@egw/config'
import { OverviewApexHoldings } from '../../../components'
import { InvestmentsOverviewOverflowMenu } from '../../../components/investment-overview-overflow-menu/investment-overview-overflow-menu'
import { type Config } from '../../../config/config'

// Investment account categories based on Business Rule 03 (FE-629)
const ACCOUNT_CATEGORIES = {
  brokerage: {
    titleKey: 'overview.brokerage_accounts',
    types: [
      'InvestmentBrokerageAccount',
      'InvestmentJointBrokerageTenantsInCommonAccount',
      'InvestmentJointBrokerageTenantsWithRightOfSurvivorshipAccount',
      'InvestmentJointBrokerageTenantsByEntiretyAccount',
      'InvestmentJointBrokerageCommunityPropertyAccount',
    ] as const,
  },
  retirement: {
    titleKey: 'overview.retirement_accounts',
    types: [
      'InvestmentRothIRAAccount',
      'InvestmentIRAAccount',
      'InvestmentInheritedIRAAccount',
      'InvestmentInheritedRothIRAAccount',
    ] as const,
  },
} as const

export const InvestmentOverviewRedesignPage: FC = () => {
  const config = configClient.getConfig() as Config

  const { isOpen: overflowMenuOpen, openModal: openOverflowMenu, closeModal: closeOverflowMenu } = useModalState()
  const { t } = useTranslation('features-investment')
  const navigate = useNavigate()
  const { flags } = useFlags<FeatureFlags>()

  const {
    data: wealthOverviewData,
    isLoading: wealthOverviewLoading,
    error: wealthOverviewError,
    dataUpdatedAt,
  } = useWealthOverview()
  const {
    data: assetAllocation,
    isLoading: assetAllocationLoading,
    error: assetAllocationError,
  } = usePortfolioOverviewAssetAllocation()

  const overview = wealthOverviewData?.personalPortfolio
  const accounts = overview?.accounts

  // Categorize accounts and calculate totals in a single pass (memoized for performance)
  const categorizedAccounts = useMemo((): CategorizedAccounts => {
    if (!accounts) {
      return {
        brokerage: { accounts: [], total: 0 },
        retirement: { accounts: [], total: 0 },
      }
    }

    const result: CategorizedAccounts = {
      brokerage: { accounts: [], total: 0 },
      retirement: { accounts: [], total: 0 },
    }

    accounts.forEach((account) => {
      const accountType = account.__typename
      if (
        ACCOUNT_CATEGORIES.brokerage.types.includes(accountType as (typeof ACCOUNT_CATEGORIES.brokerage.types)[number])
      ) {
        result.brokerage.accounts.push(account)
        result.brokerage.total += account.balance.accountValue.amount
      } else if (
        ACCOUNT_CATEGORIES.retirement.types.includes(
          accountType as (typeof ACCOUNT_CATEGORIES.retirement.types)[number],
        )
      ) {
        result.retirement.accounts.push(account)
        result.retirement.total += account.balance.accountValue.amount
      }
    })

    return result
  }, [accounts])

  const currencyCode = overview?.balance.accountValue.currencyCode ?? DEFAULT_CURRENCY_CODE

  // Filter companion accounts for holdings display
  const companionAccounts = accounts
    ? accounts.filter(
        (account) =>
          account.__typename === 'InvestmentBrokerageCompanionAccount' ||
          account.__typename === 'InvestmentIRACompanionAccount' ||
          account.__typename === 'InvestmentRothIRACompanionAccount',
      )
    : []

  const accountValue = overview?.balance.accountValue
  const investedBalance = overview?.balance.investedBalance
  const cashBalance = overview?.balance.cashBalance
  const returnSinceInception = overview?.balance.amountSinceInception

  const lastRefreshed = dataUpdatedAt ? getLastRefreshedText({ t, timestamp: dataUpdatedAt }) : undefined

  return (
    <>
      {/* Page Header - Always show */}
      <PageHeaderRedesign
        subtitle={t('overview.account_overview').toUpperCase()}
        title={t('overview.evergreen_accounts')}
        backLinkUrl={WEALTH_ROUTES.OVERVIEW.path}
        lastRefreshed={lastRefreshed}
      />

      <div className="egw-container">
        {/* Account Overview Section */}
        {wealthOverviewLoading ? (
          <div className="bg-background-main rounded-lg p-md shadow-lg flex flex-col mt-lg mb-md">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton height="100px" className="mb-4" />
            </SkeletonTheme>
          </div>
        ) : null}
        {wealthOverviewError ? (
          <div className="bg-background-main rounded-lg p-md shadow-lg flex flex-col mt-lg mb-md">
            <InformationCircleIcon className="w-8 h-8 fill-red-500 mb-2" />
            <p className="text-error text-lg font-light mb-2">{t('common.generic_error')}</p>
            <p className="text-body text-sm">{t('overview.error')}</p>
          </div>
        ) : null}
        {overview && !wealthOverviewLoading && !wealthOverviewError ? (
          <div className="overview-card mb-md">
            <h3 className="typo-primary-sm-light text-text-header">{t('investment.total_account_value')}</h3>

            <Collapse
              className="items-baseline"
              chevronClassName="text-icons-muted"
              title={
                <p className="typo-primary-2xl-regular text-text-header">
                  {accountValue ? <span>{getCurrencySymbol(accountValue.currencyCode)}</span> : null}
                  <span>
                    {accountValue?.amount
                      ? Currency.integerCents.onlyMinus(accountValue.amount)
                      : Currency.integerCents.onlyMinus(0)}
                  </span>
                </p>
              }
            >
              <div className="flex flex-col gap-2xs mb-sm mt-lg">
                <div className="flex typo-primary-base-regular text-text-meta">
                  <p className="flex-1">{t('investment.invested_balance')}</p>
                  <div>
                    {investedBalance ? <span>{getCurrencySymbol(investedBalance.currencyCode)}</span> : null}
                    <span>
                      {investedBalance?.amount
                        ? Currency.integerCents.onlyMinus(investedBalance.amount)
                        : Currency.integerCents.onlyMinus(0)}
                    </span>
                  </div>
                </div>
                <div className="flex typo-primary-base-regular text-text-meta">
                  <p className="flex-1">{t('investment.cash_balance')}</p>
                  <div>
                    {cashBalance ? <span>{getCurrencySymbol(cashBalance.currencyCode)}</span> : null}
                    <span>
                      {cashBalance?.amount
                        ? Currency.integerCents.onlyMinus(cashBalance.amount)
                        : Currency.integerCents.onlyMinus(0)}
                    </span>
                  </div>
                </div>
              </div>
              {returnSinceInception ? (
                <>
                  <hr className="mb-sm" />

                  <div className="flex typo-primary-base-regular text-text-meta flex flex-row justify-between">
                    <div className="flex flex-row gap-1 items-center">
                      <p className="flex-1">{t('investment.return_since_inception')}</p>
                    </div>
                    <p>
                      <span>{getCurrencySymbol(returnSinceInception.currencyCode)}</span>
                      <span>
                        {returnSinceInception.amount
                          ? Currency.integerCents.onlyMinus(returnSinceInception.amount)
                          : Currency.integerCents.onlyMinus(0)}
                      </span>
                    </p>
                  </div>
                </>
              ) : null}
            </Collapse>
          </div>
        ) : null}
        {/* Action Buttons Section - Show when wealth overview data is available */}
        {overview && !wealthOverviewError ? (
          <div className="flex gap-sm mb-xl">
            <div className="flex-1">
              <Button
                variant={ButtonVariant.Secondary}
                className="w-full py-sm"
                testId="investment-overview-transfer-button"
                isDisabled={config.env === 'demo-dev' || config.env === 'demo-prod'}
                onClick={() => {
                  sendEvent('user_investmentTransfer_click', {
                    eventSource: 'egWealth',
                    category: 'transfer',
                    subCategory: 'intent',
                  })
                  void navigate(WEALTH_ROUTES.TRANSFER.path)
                }}
              >
                {t('investment.cta_transfer')}
              </Button>
            </div>
            <div className="flex-1">
              <Button
                variant={ButtonVariant.Secondary}
                className="w-full py-4"
                testId="investment-overview-account-details-button"
                onClick={openOverflowMenu}
              >
                {t('account_options.documents_label')}
              </Button>
            </div>
          </div>
        ) : null}
        {/* Account Links Section */}
        {wealthOverviewLoading ? (
          <div className="mb-2xl flex flex-col gap-sm">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton height="80px" className="mb-2" />
              <Skeleton height="80px" className="mb-2" />
              <Skeleton height="80px" className="mb-2" />
            </SkeletonTheme>
          </div>
        ) : null}
        {wealthOverviewError ? (
          <div className="mb-2xl">
            <div className="border border-red-200 rounded-lg p-md">
              <InformationCircleIcon className="w-6 h-6 fill-red-500 mb-2" />
              <p className="text-error text-sm">{t('common.generic_error')}</p>
            </div>
          </div>
        ) : null}
        {(categorizedAccounts.brokerage.accounts.length > 0 || categorizedAccounts.retirement.accounts.length > 0) &&
        !wealthOverviewLoading &&
        !wealthOverviewError ? (
          <div className="flex flex-col gap-xl mb-xl">
            {categorizedAccounts.brokerage.accounts.length > 0 && (
              <AccountCategorySection
                title={t(ACCOUNT_CATEGORIES.brokerage.titleKey)}
                categoryAccounts={categorizedAccounts.brokerage.accounts}
                totalValue={categorizedAccounts.brokerage.total}
                currency={currencyCode}
                t={t}
                navigate={navigate}
              />
            )}
            {categorizedAccounts.retirement.accounts.length > 0 && (
              <AccountCategorySection
                title={t(ACCOUNT_CATEGORIES.retirement.titleKey)}
                categoryAccounts={categorizedAccounts.retirement.accounts}
                totalValue={categorizedAccounts.retirement.total}
                currency={currencyCode}
                t={t}
                navigate={navigate}
              />
            )}

            <div className="flex flex-col gap-xs">
              <div className="flex flex-row justify-between">
                <p
                  data-testid="evergreen-accounts-foundation-title"
                  className="typo-primary-lg-semibold text-text-header flex flex-row items-center gap-3xs"
                >
                  {t('evergreen_accounts.donor_advised_fund')}
                </p>
              </div>
              <ActionTile
                key="donor-advised-fund"
                variant="account"
                title={t('evergreen_accounts.donor_advised_fund')}
                description={t(`evergreen_accounts.donor_advised_fund_description`)}
                onClick={() => {
                  void navigate(WEALTH_ROUTES.FOUNDATION_ACCOUNT.path)
                }}
                testId="evergreen-accounts-foundation-link"
                className="group"
                value={
                  <p className="typo-primary-base-semibold text-text-action group-hover:underline whitespace-nowrap">
                    {t(`evergreen_accounts.learn_more`)}
                  </p>
                }
              />
            </div>
          </div>
        ) : null}
        {/* Holdings Section - Requires both wealth overview and asset allocation data */}
        {wealthOverviewLoading || assetAllocationLoading ? (
          <div className="mb-2xl">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton height="200px" className="mb-4" />
            </SkeletonTheme>
          </div>
        ) : null}
        {assetAllocationError ? (
          <Alert className="mb-2xl" title={t('holdings.error_loading_holdings')} alertType={AlertType.Error} />
        ) : null}
        {assetAllocation && companionAccounts.length > 0 ? (
          <div className="mb-2xl">
            <OverviewApexHoldings companionAccounts={companionAccounts} assetAllocation={assetAllocation} />
          </div>
        ) : null}
        <ModalDrawer title={t('account_options.documents_label')} isOpen={overflowMenuOpen} onClose={closeOverflowMenu}>
          <InvestmentsOverviewOverflowMenu />
        </ModalDrawer>
      </div>
    </>
  )
}

// Interface for categorized accounts result
interface CategorizedAccounts {
  brokerage: { accounts: InvestmentAccountDetail[]; total: number }
  retirement: { accounts: InvestmentAccountDetail[]; total: number }
}

// Reusable component for rendering account category sections
const AccountCategorySection: FC<{
  title: string
  categoryAccounts: InvestmentAccountDetail[]
  totalValue: number
  currency: string
  t: (_key: string) => string
  navigate: ReturnType<typeof useNavigate>
}> = ({ title, categoryAccounts, totalValue, currency, t, navigate }) => (
  <div className="flex flex-col gap-xs">
    <div className="flex flex-row justify-between">
      <p
        data-testid="investment-category-title"
        className="typo-primary-lg-semibold text-text-header flex flex-row items-center gap-3xs"
      >
        {title}
      </p>
      <p className="typo-primary-lg-regular text-text-header">
        {currency ? <span>{getCurrencySymbol(currency)}</span> : null}
        <span>{Currency.integerCents.onlyMinus(totalValue)}</span>
      </p>
    </div>
    {categoryAccounts.map((account) => (
      <ActionTile
        key={account.accountNumber}
        variant="account"
        title={account.accountName}
        description={t(`account_type.${account.__typename}`)}
        value={Currency.integerCents.omitSign(account.balance.accountValue.amount)}
        onClick={() => {
          void navigate(WEALTH_ROUTES.INVESTMENTS_ACCOUNT.path.replace(':alias', account.alias.toString()))
        }}
        testId={`investment-overview-${account.__typename}-link`}
      />
    ))}
  </div>
)
